

import { ClientApp } from "models";
import React, { FC, useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from 'react-router-dom';
import { TEMPLATES } from "constants/templates";
import { GetWebsiteAnalysisQuery } from "generated/graphql";
import { getProducts, Product } from "utils/getProducts";
import { existsAndNotEmpty } from "stentor-utils";

import { Onboarding, OnboardingStep } from "@xapp/components";

import { CreateProps } from "../models";
import { AnalyzeWebsiteStepContainer } from "./AnalyzeWebsiteStepContainer";
import { ConfigureAndCreateStepContainer } from "./ConfigureAndCreateStepContainer";
import { CheckoutStepContainer } from "./CheckoutStepContainer";
import { IntegrateStepContainer } from "./IntegrateStepContainer";

import style from "./style.scss";
import { CMSType, determineCMS } from "../utils/determineCMS";

function useQueryParams() {
    return new URLSearchParams(useLocation().search);
}

function useAddQueryParams() {
    const history = useHistory();

    type QueryParamsType = { [key: string]: string };
    return useCallback((newParams: QueryParamsType) => {
        const searchParams = new URLSearchParams(history.location.search);
        for (const key in newParams) {
            const value = newParams[key];
            if (value && value !== "null") {
                searchParams.set(key, newParams[key]);
            }
        }
        history.replace({ ...history.location, search: searchParams.toString() });
    }, [history]);
}

export const OnboardingContainer: FC = () => {

    const history = useHistory();

    const setQueryParams = useAddQueryParams();
    const queryParams = useQueryParams();

    const currentStep = queryParams.get("step");
    const email = queryParams.get("email");
    const website = queryParams.get("website");
    const appId = queryParams.get("appId");
    const organizationId = queryParams.get("organizationId");

    const beta = queryParams.get("beta");

    const cms = queryParams.get("cms");
    const partner = queryParams.get("partner");
    const product = queryParams.get("product");
    const coupon = queryParams.get("coupon");

    const isStarter: boolean = !product || product === "starter";

    // These keep track of the props that will create the app as we move steps
    const [appProps, setAppProps] = useState<Partial<CreateProps>>(() => {
        // in case the user refreshes, we store them (down below) on session storage
        // and try to retreive them
        let storageProps: Partial<CreateProps>
        try {
            storageProps = JSON.parse(sessionStorage.getItem("onboarding-appProps"));
        } catch (e) {
            // don't care about the error
        }
        return storageProps || {};
    });

    console.log(`Onboarding! partner=${partner} product=${product} step=${currentStep} website=${website} appId=${appId} orgId=${organizationId} coupon=${coupon}`);

    // Synchronize the query string with the current step
    useEffect(() => {
        const searchParams = new URLSearchParams(history.location.search);
        const queryStep = searchParams.get("step");
        if (currentStep !== queryStep) {
            if (currentStep) {
                searchParams.set("step", currentStep);
            } else {
                searchParams.delete("step");
            }

            if (history.location.search !== searchParams.toString()) {
                history.replace({ ...history.location, search: searchParams.toString() });
            }
        }
    }, [currentStep, history]);

    /** Transition back to "welcome" */
    const handleConfigureBack = useCallback(() => {
        setQueryParams({
            website,
            step: "welcome",
            product,
            coupon,
            partner,
            email,
            cms,
            beta
        });
    }, [beta, cms, coupon, email, partner, product, setQueryParams, website])

    /**
     *
     * Transition to step "configure"
     *
     * This performs the initial parsing of the WebSite Data
     *
     **/
    const handleSiteData = useCallback((website: string, siteData: GetWebsiteAnalysisQuery) => {

        const data = siteData?.onboarding?.webDetailsExecution?.data;

        const name = data?.business?.name;
        const primaryColor = data?.site?.primaryColor;
        const logo = data?.site?.logo;
        const businessDescription = data?.site?.description;
        const category = data?.business?.category;

        const leadsContact = email;

        const technologies = data?.detectedTechnologies;

        const cms: CMSType = determineCMS(technologies);

        const initialAppProps: Partial<CreateProps> = {
            businessDescription,
            logo,
            name,
            primaryColor,
            website,
            leadsContact,
            technologies,
            cms
        }

        if (existsAndNotEmpty(technologies)) {
            const debugTechnology: string[] = (technologies || []).filter((tech) => {
                return tech.installed;
            }).map((tech) => {
                return tech.technology;
            });

            console.log(`Technologies detected: ${debugTechnology.join(",")}`)
        }

        if (existsAndNotEmpty(data?.business?.phoneNumbers)) {
            // try to get phone number
            const firstNumber = data.business.phoneNumbers[0].number;
            if (firstNumber) {
                initialAppProps.leadsContactPhone = firstNumber;
            }
        }

        if (category) {
            const foundMatchingTemplate = TEMPLATES.find((template) => template.id === category);
            if (foundMatchingTemplate) {
                initialAppProps.template = foundMatchingTemplate;
            }
        }

        setAppProps((prevAppProps) => {

            const newAppProps: Partial<CreateProps> = {
                ...prevAppProps,
                ...initialAppProps
            }

            // save them to local storage too
            sessionStorage.setItem("onboarding-appProps", JSON.stringify(newAppProps));

            return newAppProps;
        });

        setQueryParams({
            website,
            step: "configure",
            product,
            coupon,
            partner,
            email,
            cms,
            beta
        });

    }, [beta, coupon, email, partner, product, setQueryParams]);

    /** Transition to step "integrate"  */
    const handleAppCreated = useCallback((createdApp: Pick<ClientApp, "appId" | "organizationId">) => {
        // clear these for next time
        sessionStorage.removeItem("onboarding-appProps");

        setQueryParams({
            appId: createdApp.appId,
            organizationId: createdApp.organizationId,
            website,
            step: "integrate",
            product,
            coupon,
            partner,
            email,
            cms,
            beta
        });
    }, [beta, cms, coupon, email, partner, product, setQueryParams, website]);

    const handleUpgrade = useCallback(() => {
        const searchParams = new URLSearchParams(history.location.search);

        let leadsProduct: Product;

        const products = getProducts({ partner });

        // find leads
        if (partner) {
            leadsProduct = products.find((product) => {
                return product.name.includes("Leads");
            });
        } else {
            leadsProduct = products.find((product) => {
                return product.name === "XAPP AI Leads";
            });
        }

        // this is when they request to upgrade to leads and we need track it for payment
        if (leadsProduct) {
            searchParams.set("product", leadsProduct.id);
        }

        if (history.location.search !== searchParams.toString()) {
            history.replace({ ...history.location, search: searchParams.toString() });
        }

    }, [history, partner]);

    /** Transition to step "checkout" */
    const handleIntegrateComplete = useCallback(() => {
        setQueryParams({
            appId,
            organizationId,
            website,
            step: "checkout",
            product,
            coupon,
            partner,
            email,
            cms,
            beta
        })
    }, [beta, setQueryParams, appId, organizationId, website, product, coupon, partner, email, cms]);

    let welcomeMessage: string;

    switch (partner) {
        case "lacrm":
            welcomeMessage = "Welcome Less Annoying CRM Customers!";
            break;
        case "jobber":
            welcomeMessage = "Welcome Jobber Customers!";
            break;
        case "sfl":
            welcomeMessage = "Welcome Surefire Local Customers!";
            break;
        default:
            welcomeMessage = "Welcome!";
    }

    // TODO: check to see if they have existing orgs and ask if they want to apply this to an existing app or create a new one.

    return (
        <>
            <Onboarding>
                <OnboardingStep title={"Welcome"} active={!currentStep || currentStep === "welcome"}>
                    <AnalyzeWebsiteStepContainer
                        welcomeMessage={welcomeMessage}
                        website={website}
                        className={style.stepWrapper}
                        onNextStep={handleSiteData}
                    />
                </OnboardingStep>
                <OnboardingStep title={"Configure"} active={currentStep === "configure"}>
                    <ConfigureAndCreateStepContainer
                        initialProps={appProps}
                        product={product}
                        className={style.stepWrapper}
                        onNextStep={handleAppCreated}
                        onBack={handleConfigureBack}
                    />
                </OnboardingStep>
                <OnboardingStep title={"Integrate"} active={currentStep === "integrate"}>
                    <IntegrateStepContainer
                        app={{
                            appId,
                            organizationId
                        }}
                        /** Based on if they are starter or not */
                        integrationsSupported={!isStarter}
                        limitIntegrations={partner ? [partner] : undefined}
                        className={style.stepWrapper}
                        onUpgrade={handleUpgrade}
                        onNextStep={handleIntegrateComplete}
                    />
                </OnboardingStep>
                <OnboardingStep title={isStarter ? "Terms" : "Checkout"} active={currentStep === "checkout" || currentStep === "terms"}>
                    <CheckoutStepContainer
                        couponId={coupon}
                        productId={product}
                        disableCheckout={isStarter}
                        cms={cms}
                        partner={partner}
                        app={{
                            appId,
                            organizationId
                        }}
                        className={style.stepWrapper}
                    />
                </OnboardingStep>
            </Onboarding>
        </>
    );
};
